import { useEffect, useRef, useState } from "react";

import { OptimizationDto } from "#modules/optimizations/infra/computeOptimizations.api";
import { useRemoveOptimizationLine } from "#modules/optimizations/view/useRemoveOptimizationLine";
import { MINIMUM_ITEMS_FOR_OPTIMIZATION } from "shared";

export const useExtractOptimizationLine = ({
  optimization,
  onCancelled,
}: {
  optimization: OptimizationDto;
  onCancelled: () => void;
}) => {
  const { removeOptimizationLine, isPending } = useRemoveOptimizationLine({
    quoteRequestId: optimization.quoteRequestId,
  });
  const [pendingLineId, setPendingLineId] = useState<string | null>(null);
  const wasActiveOnOpen = useRef(!optimization.deletedAt);

  useEffect(() => {
    if (wasActiveOnOpen.current && optimization.deletedAt) {
      onCancelled();
    }
  }, [optimization.deletedAt, onCancelled]);

  const extractLine = (lineId: string) =>
    removeOptimizationLine({ optimizationId: optimization.id, lineId });

  const requestExtract = (lineId: string) => {
    if (optimization.quoteRequestLines.length <= MINIMUM_ITEMS_FOR_OPTIMIZATION) {
      setPendingLineId(lineId);
      return;
    }
    extractLine(lineId);
  };

  const confirmExtract = () => {
    if (!pendingLineId) {
      return;
    }
    extractLine(pendingLineId);
    setPendingLineId(null);
  };

  return {
    requestExtract,
    confirmExtract,
    cancelExtract: () => setPendingLineId(null),
    isConfirmOpen: pendingLineId !== null,
    isExtractPending: isPending,
  };
};
